import {
  CONTEXT_NODE_TYPE,
  VIRTUAL_CUSTOMER_PARENT,
} from "common/Constants/contextNode.constants";
import { TAG_SOURCE_CONNECTORS } from "pages/Admin/Customer/Customers.constants";
import { appendVirtualCustomerParentIfAllowed } from "common/utils/contextNodeParentUtils";
import {
  isFiniteNumber,
  isNullOrUndefined,
} from "common/utils/valueChecks";

function toDisplayText(value) {
  if (isNullOrUndefined(value)) return "";
  return String(value);
}

export function buildTagRowKey(source, sourceTag) {
  return `${String(source ?? "").trim()}:${String(sourceTag ?? "").trim()}`;
}

export function parentNodeFromContextPath(context, customerName) {
  const path = String(context ?? "").trim();
  if (!path) {
    return "";
  }
  const parts = path.split("/").filter(Boolean);
  if (parts.length <= 1) {
    return customerName != null && String(customerName).trim() !== ""
      ? String(customerName).trim()
      : VIRTUAL_CUSTOMER_PARENT.DEFAULT_LABEL;
  }
  return parts.slice(0, -1).join("/");
}

/** Maps GET /v1/tags list item (platform-admin unified tag DTO) to DataTable row. */
export function mapTagSummaryToRow(dto) {
  if (!dto || typeof dto !== "object") {
    dto = {};
  }
  const source = toDisplayText(dto.source);
  const sourceTag = toDisplayText(dto.sourceTag);
  const contextTag = toDisplayText(dto.contextTag);
  const context = toDisplayText(dto.context);
  const nodeTypeRaw = dto.nodeType;
  const isContextualized =
    dto.isContextualized != null
      ? Boolean(dto.isContextualized)
      : nodeTypeRaw != null && String(nodeTypeRaw).trim() !== "";
  const rowKey = buildTagRowKey(source, sourceTag);
  const contextNodeId = isFiniteNumber(dto.contextNodeId)
    ? Number(dto.contextNodeId)
    : null;
  return {
    id: rowKey,
    rowKey,
    source,
    sourceTag,
    uncontextualized: !isContextualized,
    contextualized: isContextualized,
    nodeId: contextNodeId,
    contextNodeId,
    metricId: null,
    nodeName: toDisplayText(dto.displayName),
    nodeType: toDisplayText(nodeTypeRaw),
    nodeSubType: toDisplayText(dto.nodeSubType),
    parentNodeId: null,
    parentNodeName: "",
    parentNode: "",
    sourceSystem: source,
    sourceConnector: source,
    contextTag: contextTag || context,
    context,
    description: toDisplayText(dto.description),
    units: toDisplayText(dto.units),
    varType: toDisplayText(dto.varType),
    locid: toDisplayText(dto.locid),
    locName: toDisplayText(dto.locName),
    isRealTime: dto.isRealTime,
  };
}

export function buildUnifiedContextualizeTagsFromRows(rows) {
  const seen = new Set();
  const tags = [];
  for (const row of rows ?? []) {
    if (!row?.uncontextualized) continue;
    const source = String(row?.source ?? "").trim();
    const sourceTag = String(row?.sourceTag ?? "").trim();
    if (!source || !sourceTag) continue;
    const dedupeKey = `${source}:${sourceTag}`.toLowerCase();
    if (seen.has(dedupeKey)) continue;
    seen.add(dedupeKey);
    tags.push({ source, sourceTag });
  }
  return tags;
}

export function canBulkContextualizeTagRow(row) {
  if (!row?.uncontextualized) return false;
  const source = String(row?.source ?? "").trim();
  const sourceTag = String(row?.sourceTag ?? "").trim();
  return source !== "" && sourceTag !== "";
}

export function formatParentNameWithId(name, nodeId, { hideId = false } = {}) {
  if (hideId) {
    return name != null ? String(name).trim() : "";
  }
  const trimmed = name != null ? String(name).trim() : "";
  const idNum = isFiniteNumber(nodeId) ? Number(nodeId) : NaN;
  const idOk = idNum && Number.isFinite(idNum);
  if (trimmed && idOk) {
    return `${trimmed} - ${idNum}`;
  }
  if (trimmed) return trimmed;
  if (idOk) return String(idNum);
  return "";
}

export function getSourceTagForTagRow(row) {
  if (row == null) {
    return null;
  }
  const sourceTag = String(row.sourceTag ?? "").trim();
  return sourceTag !== "" ? sourceTag : null;
}

export function resolveContextNodeIdForTagRow(row) {
  if (row == null || row.uncontextualized) {
    return null;
  }
  const raw = row.contextNodeId ?? row.nodeId;
  if (raw == null || String(raw).trim() === "") {
    return null;
  }
  const nodeId = Number(raw);
  return Number.isFinite(nodeId) ? nodeId : null;
}

export function mapContextNodeToTagRow(dto) {
  const nodeId = dto.nodeId;
  const edgeTag = toDisplayText(dto.edgeTag);
  const sourceTag = toDisplayText(dto.sourceTag ?? edgeTag);
  const contextualizedTag = toDisplayText(dto.contextualizedTag);
  const parentNodeId = dto.parentNodeId;
  const parentNodeName = toDisplayText(dto.parentNodeName);
  const sourceSystem = toDisplayText(dto.sourceSystem);

  return {
    id: nodeId,
    nodeId,
    sourceTag,
    nodeName: toDisplayText(dto.nodeName),
    nodeType: toDisplayText(dto.nodeType || "tag"),
    nodeSubType: toDisplayText(dto.nodeSubType ?? dto.nodeSubtype),
    parentNodeId,
    parentNodeName,
    parentNode: parentNodeId != null ? String(parentNodeId) : "",
    sourceSystem,
    sourceConnector: sourceSystem,
    contextTag: contextualizedTag,
    description: toDisplayText(dto.description),
    sparkplugTagPath: edgeTag,
    path: dto.path,
    depth: dto.depth,
    updatedAt: dto.updatedAt,
    updatedBy: dto.updatedBy,
  };
}

export function mapUncontextualizedTagToRow(dto) {
  const metricId = dto.metricId;
  const sourceTag = toDisplayText(dto.tag);
  return {
    id: metricId,
    metricId,
    nodeId: null,
    uncontextualized: true,
    nodeName: toDisplayText(dto.name),
    nodeType: "",
    nodeSubType: "",
    parentNodeId: null,
    parentNodeName: "",
    parentNode: "",
    sourceSystem: toDisplayText(dto.sourceSystem ?? dto.source),
    sourceConnector: toDisplayText(dto.sourceSystem ?? dto.source),
    sourceTag,
    contextTag: "",
    description: toDisplayText(dto.description),
    dataType: toDisplayText(dto.dataType),
    sparkplugTagPath: sourceTag,
  };
}

export function isTagsBulkEditSelectionAllowed(selectedRows) {
  if (!Array.isArray(selectedRows) || selectedRows.length === 0) return true;
  const hasUn = selectedRows.some((r) => r.uncontextualized);
  const hasCtx = selectedRows.some((r) => !r.uncontextualized);
  if (hasUn && hasCtx) return false;
  if (hasUn) {
    return selectedRows.every((r) => r.uncontextualized);
  }
  const first = String(selectedRows[0]?.nodeType ?? "");
  return selectedRows.every((r) => String(r?.nodeType ?? "") === first);
}

export function buildTagSubtypeOptions(referencePayload) {
  const nodeTypes = referencePayload?.nodeTypes;
  if (!Array.isArray(nodeTypes)) return [];
  const tagType = nodeTypes.find(
    (t) => t?.name === "tag" || String(t?.name).toLowerCase() === "tag"
  );
  const subTypes = tagType?.subTypes;
  if (!Array.isArray(subTypes)) return [];
  return subTypes.map((st) => ({
    value: String(st.name),
    label: st.displayName ?? st.name,
  }));
}

export function buildNodeTypeOptions(referencePayload) {
  const nodeTypes = referencePayload?.nodeTypes;
  if (!Array.isArray(nodeTypes)) return [];
  return nodeTypes.map((nodeTypeEntry) => ({
    value: String(nodeTypeEntry.name),
    label: nodeTypeEntry.displayName ?? nodeTypeEntry.name,
  }));
}

export function getRowSourceConnector(row) {
  return String(
    row?.sourceConnector ?? row?.sourceSystem ?? row?.source ?? ""
  ).trim();
}

export function isWmAppConnector(sourceConnector) {
  return (
    String(sourceConnector ?? "").trim().toLowerCase() ===
    TAG_SOURCE_CONNECTORS.WM_APP
  );
}

export function filterNodeTypeOptionsForTagEdit(
  nodeTypeOptions,
  sourceConnector = ""
) {
  const excludeTag = isWmAppConnector(sourceConnector);
  return (nodeTypeOptions ?? []).filter((option) => {
    const value = String(option?.value ?? "").toLowerCase();
    if (value === CONTEXT_NODE_TYPE.CUSTOMER) return false;
    if (excludeTag && value === CONTEXT_NODE_TYPE.TAG) return false;
    return true;
  });
}

export function resolveNodeTypeForTagEdit(row) {
  const connector = getRowSourceConnector(row);
  if (connector && !isWmAppConnector(connector)) {
    return CONTEXT_NODE_TYPE.TAG;
  }
  const nodeType = String(row?.nodeType ?? "");
  if (
    isWmAppConnector(connector) &&
    nodeType.toLowerCase() === CONTEXT_NODE_TYPE.TAG
  ) {
    return "";
  }
  return nodeType;
}

export function isNodeTypeLockedForSourceTag(row) {
  const connector = getRowSourceConnector(row);
  if (connector && !isWmAppConnector(connector)) {
    return true;
  }
  const sourceTag = String(row?.sourceTag ?? "").trim();
  if (!sourceTag) return false;
  if (row?.uncontextualized) return true;
  if (isWmAppConnector(connector)) return false;
  return String(row?.nodeType ?? "").toLowerCase() === CONTEXT_NODE_TYPE.TAG;
}

export function buildSubtypeOptionsByNodeType(referencePayload) {
  const nodeTypes = referencePayload?.nodeTypes;
  if (!Array.isArray(nodeTypes)) return {};
  return nodeTypes.reduce((accumulator, nodeTypeEntry) => {
    const key = String(nodeTypeEntry.name);
    const subtypeOptions = Array.isArray(nodeTypeEntry.subTypes)
      ? nodeTypeEntry.subTypes.map((subTypeEntry) => ({
          value: String(subTypeEntry.name),
          label: subTypeEntry.displayName ?? subTypeEntry.name,
        }))
      : [];
    accumulator[key] = subtypeOptions;
    return accumulator;
  }, {});
}

export function getAllowedParentTypesForNodeType(referencePayload, nodeTypeName) {
  const nodeTypes = referencePayload?.nodeTypes;
  if (!Array.isArray(nodeTypes)) return [];
  const key = String(nodeTypeName || "").toLowerCase();
  const entry = nodeTypes.find(
    (t) => String(t?.name || "").toLowerCase() === key
  );
  const allowed = entry?.allowedParentTypes;
  if (!Array.isArray(allowed)) return [];
  return allowed.map((t) => String(t).toLowerCase());
}

export function collectUnionOfAllowedParentTypes(referencePayload) {
  const nodeTypes = referencePayload?.nodeTypes;
  if (!Array.isArray(nodeTypes)) return [];
  const union = new Set();
  for (const nt of nodeTypes) {
    const allowed = nt?.allowedParentTypes;
    if (!Array.isArray(allowed)) continue;
    for (const p of allowed) {
      const name = String(p ?? "").trim().toLowerCase();
      if (name) union.add(name);
    }
  }
  return [...union];
}

export function resolveParentCandidatesForNodeType(
  referencePayload,
  nodeType,
  parentCandidates,
  customerName
) {
  let allowed = getAllowedParentTypesForNodeType(referencePayload, nodeType);
  if (
    String(nodeType ?? "").toLowerCase() === CONTEXT_NODE_TYPE.DOMAIN &&
    !allowed.includes(CONTEXT_NODE_TYPE.CUSTOMER)
  ) {
    allowed = [...allowed, CONTEXT_NODE_TYPE.CUSTOMER];
  }
  if (!allowed.length) {
    return [];
  }
  const candidates = Array.isArray(parentCandidates) ? parentCandidates : [];
  const allowedSet = new Set(allowed);
  const byId = new Map();
  candidates.forEach((candidate) => {
    if (!allowedSet.has(String(candidate?.nodeType ?? "").toLowerCase())) {
      return;
    }
    const key = String(candidate?.nodeId);
    if (!key || byId.has(key)) {
      return;
    }
    byId.set(key, candidate);
  });
  appendVirtualCustomerParentIfAllowed(byId, allowed, customerName);
  return [...byId.values()];
}

export function buildParentSelectOptionsForRow(
  row,
  referencePayload,
  parentCandidates,
  customerName
) {
  const resolvedCandidates = resolveParentCandidatesForNodeType(
    referencePayload,
    row?.nodeType,
    parentCandidates,
    customerName
  );
  return resolvedCandidates.map((candidate) => ({
    value: String(candidate.nodeId),
    label: formatParentNameWithId(candidate.nodeName, candidate.nodeId),
  }));
}

export function mergeParentCandidatesFromTagRows(rows, referencePayload) {
  if (!referencePayload?.nodeTypes || !Array.isArray(rows) || rows.length === 0) {
    return [];
  }
  const byId = new Map();
  for (const row of rows) {
    const pid = row.parentNodeId;
    if (isNullOrUndefined(pid)) continue;
    if (!isFiniteNumber(pid)) continue;
    const pidNum = Number(pid);

    const allowed = getAllowedParentTypesForNodeType(
      referencePayload,
      row?.nodeType
    );

    if (pidNum === 0) {
      if (byId.has("0")) continue;
      const name =
        row.parentNodeName != null && String(row.parentNodeName).trim() !== ""
          ? String(row.parentNodeName).trim()
          : VIRTUAL_CUSTOMER_PARENT.DEFAULT_LABEL;
      const allowedLower = allowed.map((t) => String(t).toLowerCase());
      const customerIdx = allowedLower.indexOf(CONTEXT_NODE_TYPE.CUSTOMER);
      const nodeType =
        customerIdx >= 0
          ? allowed[customerIdx]
          : allowed.length > 0
            ? allowed[0]
            : CONTEXT_NODE_TYPE.CUSTOMER;
      byId.set(String(VIRTUAL_CUSTOMER_PARENT.NODE_ID), {
        nodeId: VIRTUAL_CUSTOMER_PARENT.NODE_ID,
        nodeName: name,
        nodeType: String(nodeType).trim().toLowerCase(),
        parentNodeId: null,
      });
      continue;
    }

    if (allowed.length !== 1) continue;
    const nodeType = allowed[0];
    const key = String(pid);
    if (byId.has(key)) continue;
    const name =
      row.parentNodeName != null && String(row.parentNodeName).trim() !== ""
        ? String(row.parentNodeName)
        : String(pid);
    byId.set(key, {
      nodeId: Number(pid),
      nodeName: name,
      nodeType,
    });
  }
  return [...byId.values()];
}

export function buildParentFilterOptions(parentCandidates) {
  if (!Array.isArray(parentCandidates) || parentCandidates.length === 0) return [];
  return [...parentCandidates]
    .map((c) => ({
      value: String(c.nodeId),
      label: formatParentNameWithId(c.nodeName, c.nodeId),
    }))
    .sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
    );
}

function resolveNodeIdForPatch(nodeId) {
  if (nodeId == null || String(nodeId).trim() === "") {
    return null;
  }
  if (String(nodeId).startsWith("unctx:")) {
    return null;
  }
  if (typeof nodeId === "number" && Number.isFinite(nodeId)) {
    return nodeId;
  }
  const trimmed = String(nodeId).trim();
  const parsed = Number(trimmed);
  if (trimmed !== "" && Number.isFinite(parsed) && String(parsed) === trimmed) {
    return parsed;
  }
  return trimmed;
}

export function buildPatchBodyForCellUpdate(nodeId, fieldId, value) {
  const resolvedNodeId = resolveNodeIdForPatch(nodeId);
  if (resolvedNodeId == null) {
    return null;
  }
  const nodeIds = [resolvedNodeId];

  if (fieldId === "nodeName") {
    return { nodeIds, nodeName: value };
  }
  if (fieldId === "nodeSubType") {
    return { nodeIds, nodeSubType: value };
  }
  if (fieldId === "nodeType") {
    return { nodeIds, nodeType: value };
  }
  if (fieldId === "parentNode") {
    const parsedParentNodeId = Number(value);
    if (isFiniteNumber(parsedParentNodeId)) {
      return { nodeIds, parentNodeId: parsedParentNodeId };
    }
    return null;
  }
  return null;
}

export function buildPatchBodyForBulkUpdate(nodeIds, patch) {
  const body = { nodeIds: [...nodeIds] };
  if (patch.nodeType != null) body.nodeType = patch.nodeType;
  if (patch.nodeName != null) body.nodeName = patch.nodeName;
  if (patch.nodeSubType !== undefined) body.nodeSubType = patch.nodeSubType;
  if (patch.parentNodeId != null && patch.parentNodeId !== "") {
    const pid =
      typeof patch.parentNodeId === "string"
        ? Number(patch.parentNodeId)
        : patch.parentNodeId;
    if (isFiniteNumber(pid)) body.parentNodeId = pid;
  }
  return body;
}
